import { useCallback, useEffect, useRef, useState } from 'react'
import type { JSX } from 'react'

import {
  Button,
  ChevronDownIcon,
  Flex,
  Tag as PopcornTag,
  TagIcon,
  Text,
} from '@buffer-mono/popcorn'

import { TagsCombobox } from '~publish/components/TagsCombobox'
import { useTagsQuery } from '~publish/hooks/useTags'
import type { SelectedTag } from '~publish/legacy/tags/types'

import styles from './TagsSelector.module.css'

type Props = {
  onSelectTags: (selectedTag: SelectedTag[]) => void
  preSelectedTags?: SelectedTag[]
  disabled?: boolean
  onClose?: () => void
}

const MAX_TAGS_PER_POST = 10
const maxVisibleTags = 2

export const TagsSelector = ({
  onSelectTags,
  preSelectedTags,
  disabled = false,
  onClose,
}: Props): JSX.Element => {
  const { tags, loading } = useTagsQuery()

  const [open, setOpen] = useState(false)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const buttonRef = useRef(null)

  // Sync preSelectedTags with internal state
  useEffect(() => {
    if (preSelectedTags) {
      // Filter out locked tags
      const availableTagIds = preSelectedTags
        .map((tag) => tag.id)
        .filter((id) => {
          const tag = tags.find((t) => t.id === id)
          return tag && !tag.isLocked
        })
      setSelectedTagIds(availableTagIds)
    }
  }, [preSelectedTags, tags])

  // Convert tag IDs to SelectedTag format and notify parent
  const handleChange = useCallback(
    (tagIds: string[]) => {
      setSelectedTagIds(tagIds)
      const selectedTags: SelectedTag[] = tagIds
        .map((id) => {
          const tag = tags.find((t) => t.id === id)
          return tag ? { id: tag.id, name: tag.name, color: tag.color } : null
        })
        .filter((tag): tag is SelectedTag => tag !== null)
      onSelectTags(selectedTags)
    },
    [tags, onSelectTags],
  )

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      setOpen(newOpen)
      if (!newOpen && onClose) {
        onClose()
      }
    },
    [onClose],
  )

  if (loading) {
    return <div className={styles.wrapper}>Loading...</div>
  }

  const selectedTags = tags.filter((tag) => selectedTagIds.includes(tag.id))
  const label = selectedTags.length === 0 ? 'Tags' : 'Add and edit tags'
  const visibleTags =
    maxVisibleTags < selectedTags.length
      ? selectedTags.slice(0, maxVisibleTags)
      : selectedTags
  const remainingTagsCount = selectedTags.length - visibleTags.length

  return (
    <div className={styles.wrapper} data-testid="tag-selector">
      <TagsCombobox
        open={open}
        selectedTags={selectedTagIds}
        onChange={handleChange}
        onOpenChange={handleOpenChange}
        limit={MAX_TAGS_PER_POST}
        align="end"
        anchor={buttonRef}
        showFooter={false}
      >
        <Button
          ref={buttonRef}
          variant="secondary"
          disabled={disabled}
          aria-label={label}
        >
          {selectedTags.length === 0 ? (
            <Flex as="span" gap="xs" align="center">
              <TagIcon />
              Tags
            </Flex>
          ) : (
            <Flex as="span" gap="xs" align="center" className={styles.tagList}>
              {visibleTags.map((tag) => (
                <Flex key={tag.id} gap="2xs" align="center">
                  <PopcornTag
                    className={styles.tagColorSquare}
                    color={tag.color}
                  >
                    {' '}
                  </PopcornTag>
                  <span translate="no">{tag.name}</span>
                </Flex>
              ))}
              {maxVisibleTags && remainingTagsCount > 0 && (
                <Text weight="medium">+{remainingTagsCount}</Text>
              )}
            </Flex>
          )}
          <ChevronDownIcon />
        </Button>
      </TagsCombobox>
    </div>
  )
}
